import { useFlags } from 'launchdarkly-react-client-sdk'
import Router from 'next/router'
import { Fragment, ReactNode, useCallback, useState } from 'react'
import { Form } from 'react-final-form'

import get from 'lodash-es/get'

import { FEATURE_FLAGS } from '@infura/packages.common/constants'
import Notification from '@infura/packages.components/Notification'
import { ActionLink } from '@infura/packages.ui/ActionLink'
import { Button } from '@infura/packages.ui/Button'

import FieldInput from 'components/FieldInput'
import { useRefreshData, useSetSelectedApiKey } from 'components/keySelection/Context'

import { useUKSProjects } from 'hooks/useUKSProjects'
import useUser from 'hooks/useUser'

import { uks } from 'lib/api/uks'
import { BILLING_PLAN_ROUTE } from 'lib/constants'
import handleSubmissionError from 'lib/handleSubmissionError'
import { Infura } from 'lib/types/infuraTypes'

export interface CreateProjectKeyFormProps {
  onCancelClick?: () => void
  productSuggestion?: ReactNode
  selectedProduct?: Infura.ProductType
  showCancelButton?: boolean
  submitButtonLabel?: string
  afterSubmit?: () => void | Promise<void>
}

const RenderError = (error: string | ReactNode) => {
  return (
    <Notification className='mb-2' color='alert' size='sm' variant='outlined'>
      {error}
    </Notification>
  )
}

export const CreateProjectKeyForm = () => {
  const { projects: _projects } = useUKSProjects()
  const projects = Object.values(_projects || {})
  const { user } = useUser()
  const [maxKeysError, setMaxKeysError] = useState(false)
  const flags = useFlags()
  const migratingUKSUsers = flags[FEATURE_FLAGS.MIGRATING_USERS_UKS_CAPPED]
  const setSelectedKey = useSetSelectedApiKey()
  const refreshData = useRefreshData()

  const onSubmit = async ({ name }) => {
    try {
      const { data } = await uks.projectMethodsUpsert({
        name,
        settings: { basic: { description: '' } },
        // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
        userId: user.id
      })

      if (!data) {
        return handleSubmissionError(new Error('Invalid network'))
      }

      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      await uks.securityMethodsGenerate(data.result.id, {})

      setSelectedKey(data?.result?.id as string)
      refreshData()
      Router.push(`/key/all-endpoints`)
    } catch (e) {
      if (get(e, 'response.data.error.code') === 7) {
        setMaxKeysError(true)
      }
    }
  }

  const required = useCallback(value => (value ? undefined : 'Please enter a valid key name'), [])

  return (
    <>
      <Form
        initialValues={{ product: 'ethereum' }}
        onSubmit={onSubmit}
        render={({ handleSubmit, submitError, submitting }) => (
          <Fragment>
            <span>{submitError ? RenderError(<span className='text-center'>{submitError}</span>) : null}</span>
            <span>
              {migratingUKSUsers
                ? RenderError(
                    <>
                      <p className='m-0 leading-6'>
                        The “Create New Key” feature is temporarily unavailable due to a scheduled system upgrade. We
                        expect to restore full functionality within 2 hours. We appreciate your patience and
                        understanding.
                      </p>
                    </>
                  )
                : null}
            </span>
            <span>
              {maxKeysError
                ? RenderError(
                    <>
                      <p className='m-0 leading-6'>
                        <span>Key Creation Limit Reached. You have reached your maximum limit of </span>
                        <strong>{projects.length} keys</strong>
                        <span>. To create more keys, please </span>
                        <ActionLink className='font-sans text-lg normal-case' href={BILLING_PLAN_ROUTE}>
                          <span className='text-[16px] text-red-500 underline'>upgrade your plan</span>
                        </ActionLink>
                        <span>.</span>
                      </p>
                    </>
                  )
                : null}
            </span>
            <form className='w-full' data-testid='project-form' onSubmit={handleSubmit}>
              <div>
                <FieldInput
                  className={'border-gray-400'}
                  data-testid='name'
                  inputMode={'text'}
                  label={'Name'}
                  name='name'
                  type='text'
                  validate={required}
                />

                <div className='mt-6 flex flex-wrap sm:flex-nowrap'>
                  <Button
                    className='w-full'
                    data-testid='create-project-submit'
                    disabled={submitting || migratingUKSUsers}
                    loading={submitting}
                    type='submit'
                  >
                    Create
                  </Button>
                </div>
              </div>
            </form>
          </Fragment>
        )}
      />
    </>
  )
}
